import cn from 'classnames';
import { getLanguageCode } from 'config/constants/locales';
import { useConfig } from 'config/use-config';
import { useLocale } from 'hooks/use-locale';
import { StaticPDPProduct } from 'modules/product-details/types';
import Script from 'next/script';
import { FC, useLayoutEffect, useState } from 'react';
import styles from './styles.module.css';

interface PropTypes extends React.HTMLAttributes<HTMLDivElement> {
  iconSize?: number;
  sku: StaticPDPProduct['id'];
}

declare global {
  interface Window {
    ratingSnippet: (className: string, options: any) => void;
  }
}

const SNIPPET_CLASS = 'rating-snippet';

const RatingBadge: FC<PropTypes> = ({ className, sku, iconSize = 16, ...props }) => {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const { reviewsIoStoreId } = useConfig();
  const locale = useLocale();

  useLayoutEffect(() => {
    if (!window.ratingSnippet) {
      return;
    }

    /**
     * There is a bug in their implementation which happens when we "Load more" products.
     * This means that when new instances of the snippet are created, old snippets get damaged (they start showing 0 rating)
     * Removing this attribute from existing snippets before initialization of new ones fixes the issue.
     * The bug is already reported to them and they are checking it (though bouncing back to us, so not sure if it will get fixed).
     */
    document.querySelectorAll(`.${SNIPPET_CLASS}`).forEach((snippetElement) => {
      snippetElement.removeAttribute('data-retrieved');
    });

    window.ratingSnippet(SNIPPET_CLASS, {
      store: reviewsIoStoreId,
      mode: 'extended',
      color: '#FEE06E',
      linebreak: false,
      text: '',
      singularText: '',
      lang: getLanguageCode(locale),
      usePolaris: false,
      showEmptyStars: false,
      showNumericalAverageRating: false,
      enableSyndication: true,
    });
  }, [scriptLoaded, sku, reviewsIoStoreId, locale]);

  return (
    <>
      <Script
        src="https://widget.reviews.io/rating-snippet/dist.js"
        strategy="lazyOnload"
        onLoad={() => setScriptLoaded(true)}
        key="rating-badge-script"
      />
      <div
        style={{ '--rating-badge-icon-size': `${iconSize}px` } as React.CSSProperties}
        className={cn(SNIPPET_CLASS, styles.ratingSnippet, className)}
        data-sku={sku}
        key={sku}
        {...props}
      />
    </>
  );
};

export default RatingBadge;
